import {useEffect, useRef, useState} from 'react';
import {
  Badge,
  Box,
  Card,
  ChoiceList,
  IndexFilters,
  IndexFiltersMode,
  IndexTable,
  InlineStack,
  Page,
  Text,
  TextField,
  useSetIndexFiltersMode,
} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';
import {AppFrame} from '@slj/ui/app-frame';
import {EmptyState} from '@slj/ui/patterns';

import type {ChromeProps} from './dashboard';
import {identityAccount, identitySections} from './nav';

export interface AuditRow {
  at: string;
  tone: 'critical' | 'attention' | 'success' | undefined;
  label: string;
  detail: string;
  detailTitle?: string;
  actor: string | null;
  target: string;
  ip: string | null;
}

export interface AuditProps extends PagePropsBase {
  chrome: ChromeProps;
  rows: AuditRow[];
  /** Current filter state, straight from the URL (the source of truth). */
  actions: string[];
  actor: string;
  after: string;
  before: string;
  sort: 'newest' | 'oldest';
  /** Distinct actions present in the trail, with their human labels. */
  actionOptions: {label: string; value: string}[];
  page: number;
  totalPages: number;
  total: number;
  prevHref: string | null;
  nextHref: string | null;
  csvHref: string;
}

/**
 * Quick-filter tabs — each is a preset over the same `action` param the Action facet
 * writes, so tabs and the facet are one mechanism (a tab is a shortcut to an action
 * set). An ad-hoc facet selection that matches no preset simply shows the All tab with
 * applied-filter chips, exactly as the recipes browse page does with saved views.
 */
const TABS: {label: string; actions: string[]}[] = [
  {label: 'All', actions: []},
  {label: 'Sign-ins', actions: ['sign_in', 'sign_in_failed', 'sign_out']},
  {
    label: 'Access changes',
    actions: [
      'app_access_granted',
      'app_access_revoked',
      'role_changed',
      'user_banned',
      'group_created',
      'group_deleted',
      'group_member_added',
      'group_member_removed',
    ],
  },
  {label: 'Failures', actions: ['sign_in_failed', 'ci_failed']},
];

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

export function AuditPage({
  chrome,
  rows,
  actions,
  actor,
  after,
  before,
  sort,
  actionOptions,
  page,
  totalPages,
  total,
  prevHref,
  nextHref,
  csvHref,
}: AuditProps) {
  const {user} = chrome;
  const {mode, setMode} = useSetIndexFiltersMode(IndexFiltersMode.Default);

  // The URL is the source of truth: every control builds the next URL and navigates
  // (a full load — this is an SSR'd MPA, not a client-routed app). Any filter change
  // resets paging; page 3 of the old filter is not page 3 of the new one.
  const go = (changes: {
    actions?: string[];
    actor?: string;
    after?: string;
    before?: string;
    sort?: 'newest' | 'oldest';
  }) => {
    const p = new URLSearchParams();
    (changes.actions ?? actions).forEach((a) => p.append('action', a));
    const actorV = changes.actor ?? actor;
    if (actorV) p.set('actor', actorV);
    const afterV = changes.after ?? after;
    if (afterV) p.set('after', afterV);
    const beforeV = changes.before ?? before;
    if (beforeV) p.set('before', beforeV);
    const sortV = changes.sort ?? sort;
    if (sortV === 'oldest') p.set('sort', 'oldest');
    const qs = p.toString();
    window.location.assign(`/admin/audit${qs ? `?${qs}` : ''}`);
  };

  // Actor search: typed locally, committed to the URL on a debounce (a navigation),
  // so typing isn't one reload per keystroke.
  const [query, setQuery] = useState(actor);
  const committed = useRef(actor);
  useEffect(() => {
    if (query === committed.current) return;
    const t = setTimeout(() => {
      committed.current = query;
      go({actor: query});
    }, 600);
    return () => clearTimeout(t);
  }, [query]);

  const selectedTab = Math.max(
    0,
    TABS.findIndex((t) => sameSet(t.actions, actions)),
  );

  const appliedFilters: {key: string; label: string; onRemove: () => void}[] = [];
  if (actions.length && selectedTab <= 0) {
    // Only surface an action chip for an ad-hoc selection (a tab already names its set).
    const labelFor = (v: string) => actionOptions.find((o) => o.value === v)?.label ?? v;
    appliedFilters.push({
      key: 'action',
      label: `Action: ${actions.map(labelFor).join(', ')}`,
      onRemove: () => go({actions: []}),
    });
  }
  if (after) appliedFilters.push({key: 'after', label: `From ${after}`, onRemove: () => go({after: ''})});
  if (before) appliedFilters.push({key: 'before', label: `To ${before}`, onRemove: () => go({before: ''})});

  return (
    <AppFrame
      logoUrl="/assets/logo.svg"
      user={user}
      sections={identitySections(user.isAdmin)}
      currentPath={chrome.path}
      account={identityAccount}
    >
      <Page
        title="Audit"
        fullWidth
        secondaryActions={[{content: 'Export CSV', url: csvHref, external: true}]}
      >
        <Card padding="0">
          <IndexFilters
            queryValue={query}
            queryPlaceholder="Search by actor email…"
            onQueryChange={setQuery}
            onQueryClear={() => {
              setQuery('');
              go({actor: ''});
            }}
            tabs={TABS.map((t, i) => ({id: `tab-${i}`, content: t.label}))}
            selected={selectedTab}
            onSelect={(i) => go({actions: TABS[i]?.actions ?? []})}
            sortOptions={[
              {label: 'Time', value: 'newest desc', directionLabel: 'Newest first'},
              {label: 'Time', value: 'oldest asc', directionLabel: 'Oldest first'},
            ]}
            sortSelected={[sort === 'oldest' ? 'oldest asc' : 'newest desc']}
            onSort={([value]) => go({sort: value?.startsWith('oldest') ? 'oldest' : 'newest'})}
            filters={[
              {
                key: 'action',
                label: 'Action',
                filter: (
                  <ChoiceList
                    title="Action"
                    titleHidden
                    allowMultiple
                    choices={actionOptions}
                    selected={actions}
                    onChange={(values) => go({actions: values})}
                  />
                ),
                shortcut: true,
              },
              {
                key: 'from',
                label: 'From date',
                filter: (
                  <TextField
                    label="From"
                    labelHidden
                    type="date"
                    autoComplete="off"
                    value={after}
                    onChange={(v) => go({after: v})}
                  />
                ),
              },
              {
                key: 'to',
                label: 'To date',
                filter: (
                  <TextField
                    label="To"
                    labelHidden
                    type="date"
                    autoComplete="off"
                    value={before}
                    onChange={(v) => go({before: v})}
                  />
                ),
              },
            ]}
            appliedFilters={appliedFilters}
            onClearAll={() => {
              setQuery('');
              window.location.assign('/admin/audit');
            }}
            mode={mode}
            setMode={setMode}
            cancelAction={{onAction: () => setMode(IndexFiltersMode.Default)}}
          />
          <IndexTable
            resourceName={{singular: 'event', plural: 'events'}}
            itemCount={rows.length}
            selectable={false}
            headings={[
              {title: 'When'},
              {title: 'Action'},
              {title: 'Detail'},
              {title: 'Who'},
              {title: 'Target'},
              {title: 'IP'},
            ]}
            emptyState={<EmptyState density="generous">No events match.</EmptyState>}
            pagination={{
              hasPrevious: !!prevHref,
              onPrevious: () => prevHref && window.location.assign(prevHref),
              hasNext: !!nextHref,
              onNext: () => nextHref && window.location.assign(nextHref),
              label:
                total === 0
                  ? 'No events'
                  : `Page ${page} of ${totalPages} · ${total} event${total === 1 ? '' : 's'}`,
            }}
          >
            {rows.map((r, index) => (
              <IndexTable.Row id={`${index}`} key={index} position={index}>
                <IndexTable.Cell>
                  <Text as="span" tone="subdued">{r.at}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Badge tone={r.tone}>{r.label}</Badge>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Box maxWidth="34ch">
                    <Text as="span" truncate>
                      <span title={r.detailTitle}>{r.detail || '—'}</span>
                    </Text>
                  </Box>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {r.actor ?? <Text as="span" tone="subdued">anonymous</Text>}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" tone="subdued">{r.target}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text as="span" tone="subdued">{r.ip ?? '—'}</Text>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
        </Card>
      </Page>
    </AppFrame>
  );
}
